import AppCommandBar from 'app/AppCommandBar'
import { AutoStack, FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { useDeleteBlobMutation } from 'components/DocumentGenerator/documentGenerator.generated'
import { DownloadLink } from 'components/DownloadLink'
import { FileUpload } from 'components/FileUpload/FileUpload'
import { StickyPane } from 'components/StickyPane'
import { Form, Formik, FormikActions, FormikContext } from 'formik'
import { FormikCheckbox, FormikTextField } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem } from 'forms/FormStack'
import { Box } from 'grid'
import { omitDeep } from 'helpers'
import { useCommand } from 'hooks/useCommand'
import {
  ActiveProvingTypesSetting,
  Blob,
  FileSetting,
  GlobalMessageSetting,
  GlobalSettingType,
  ProvingType,
  ProvingTypeSetting,
  SingleValueSetting,
  UserRole,
} from 'model'
import { DefaultButton, Icon, PrimaryButton } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Section } from 'views/ProvingDetailsView/Layout'
import * as Yup from 'yup'
import { object } from 'yup'
import {
  useCreateOrUpdateGeneralSettingMutation,
  useGetAllSettingsQuery,
} from './AdminGeneralSettings.generated'
class SettingsFormSchema {
  constructor() {
    return Yup.object().shape({
      deletetionPeriodSetting: object().shape({
        value: Yup.string()
          .required('Löschfrist ist ein Pflichtfeld')
          .test('number', 'must be a number', (value) => !isNaN(value)),
      }),
    })
  }
}

const GeneralSettingsFormCommands: React.FC<{
  formik: FormikContext<any>
}> = ({ formik, children }) => {
  const { t } = useTranslation()
  useCommand(
    {
      key: 'save',
      text: t('action.save'),
      iconProps: { iconName: 'Save' },
      disabled: formik.isSubmitting || !formik.isValid || !formik.dirty,
      onClick: () => formik.submitForm(),
    },
    [formik.submitForm, formik.isSubmitting, formik.isValid, formik.dirty]
  )
  return <Box px={4}>{children}</Box>
}

export const AdminGeneralSettingsView: React.FC = () => {
  const { t } = useTranslation()

  const [deleteBlob] = useDeleteBlobMutation()

  const [overviewReuploadTemplate, setOverviewReuploadTemplate] =
    React.useState(false)
  const [costReuploadTemplate, setCostReuploadTemplate] = React.useState(false)
  const [overviewTemplateIsLoading, setOverviewTemplateIsLoading] =
    React.useState(false)
  const [costTemplateIsLoading, setCostTemplateIsLoading] =
    React.useState(false)

  const [createOrUpdateSetting] = useCreateOrUpdateGeneralSettingMutation()

  const { data, loading, refetch, error } = useGetAllSettingsQuery()
  const settings = data && data.globalSettings ? data.globalSettings : []

  const provingTypeSetting = settings.find(
    (s) => s.type === GlobalSettingType.ActiveProvingTypes
  ) as ActiveProvingTypesSetting | undefined

  const globalMessageSetting = settings.find(
    (s) => s.type === GlobalSettingType.GlobalMessage
  ) as GlobalMessageSetting | undefined

  const deletetionPeriodSetting = settings.find(
    (s) => s.type === GlobalSettingType.DeletionPeriod
  ) as SingleValueSetting

  const excelOverviewTemplateSetting = settings.find(
    (s) => s.type === GlobalSettingType.ExcelOverviewTemplate
  ) as FileSetting | undefined

  const costDistributionTemplateSetting = settings.find(
    (s) => s.type === GlobalSettingType.CostDistributionTemplate
  ) as FileSetting | undefined

  const existingOverviewTemplate = excelOverviewTemplateSetting
    ? excelOverviewTemplateSetting.file
    : undefined

  const existingCostTemplate = costDistributionTemplateSetting
    ? costDistributionTemplateSetting.file
    : undefined

  const settingFormValues = {
    provingTypeSetting: provingTypeSetting
      ? omitDeep(provingTypeSetting, ['_id', '__typename'])
      : getDefaultProvingTypeOptions(),
    deletetionPeriodSetting: deletetionPeriodSetting
      ? omitDeep(deletetionPeriodSetting, ['_id', '__typename'])
      : ({
          type: GlobalSettingType.DeletionPeriod,
          value: '',
        } as SingleValueSetting),
    globalMessageSetting: globalMessageSetting
      ? omitDeep(globalMessageSetting, ['_id', '__typename'])
      : {
          type: GlobalSettingType.GlobalMessage,
          globalMessage: {
            message: '',
            isActive: false,
          },
        },
  }

  const handleSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)

      await createOrUpdateSetting({
        variables: {
          input: values.provingTypeSetting,
        },
      })

      await createOrUpdateSetting({
        variables: { input: values.deletetionPeriodSetting },
      })

      await createOrUpdateSetting({
        variables: {
          input: values.globalMessageSetting,
        },
      })

      await refetch()
      actions.resetForm()
      actions.setSubmitting(false)
    },
    [createOrUpdateSetting, refetch]
  )

  const onExcelOverviewTemplateUploaded = React.useCallback(
    async (files: Partial<Blob>[]) => {
      setOverviewTemplateIsLoading(true)
      if (existingOverviewTemplate) {
        await deleteBlob({
          variables: {
            uri: existingOverviewTemplate.uri,
          },
        })
      }

      const file = files[0]
      if (file && file.containerUri && file.name && file.uri) {
        await createOrUpdateSetting({
          variables: {
            input: {
              type: GlobalSettingType.ExcelOverviewTemplate,
              file: {
                uri: file.uri!,
                containerUri: file.containerUri!,
                name: file.name!,
              },
            },
          },
        })
      }
      await refetch()
      setOverviewReuploadTemplate(false)
      setOverviewTemplateIsLoading(false)
    },
    [
      existingOverviewTemplate,
      setOverviewTemplateIsLoading,
      deleteBlob,
      createOrUpdateSetting,
      refetch,
      setOverviewReuploadTemplate,
    ]
  )

  const onCostDistributionTemplateUploaded = React.useCallback(
    async (files: Partial<Blob>[]) => {
      setCostTemplateIsLoading(true)
      if (existingCostTemplate) {
        await deleteBlob({
          variables: {
            uri: existingCostTemplate.uri,
          },
        })
      }

      const file = files[0]
      if (file && file.containerUri && file.name && file.uri) {
        await createOrUpdateSetting({
          variables: {
            input: {
              type: GlobalSettingType.CostDistributionTemplate,
              file: {
                uri: file.uri!,
                containerUri: file.containerUri!,
                name: file.name!,
              },
            },
          },
        })
      }
      await refetch()
      setCostReuploadTemplate(false)
      setCostTemplateIsLoading(false)
    },
    [
      existingCostTemplate,
      deleteBlob,
      createOrUpdateSetting,
      refetch,
      setCostReuploadTemplate,
    ]
  )

  return (
    <ViewAuthorizer roles={[UserRole.SystemAdministrator]}>
      <StickyPane>
        <AppCommandBar />
        <FlexSpinner loading={loading} error={error}>
          <Formik
            onSubmit={handleSubmit}
            initialValues={settingFormValues}
            validationSchema={new SettingsFormSchema()}
          >
            {(formik) => (
              <GeneralSettingsFormCommands formik={formik}>
                <Form>
                  <Section label={t('Aktive Erprobungs-Arten')}>
                    <FieldStack>
                      {settingFormValues.provingTypeSetting.typeSettings.map(
                        (typeSetting, i) => (
                          <FieldStackItem
                            key={i}
                            name={`provingTypeSetting.typeSettings[${i}].isActive`}
                            render={(fieldProps) => (
                              <FormikCheckbox
                                label={t(
                                  `enum.provingType.${typeSetting.type}`
                                )}
                                {...fieldProps}
                              />
                            )}
                          />
                        )
                      )}
                    </FieldStack>
                  </Section>
                  <Section label={t('Nachrichten Banner')}>
                    <FieldStack tokens={{ childrenGap: 16 }}>
                      <FieldStackItem
                        name="globalMessageSetting.globalMessage.message"
                        render={(fieldProps) => (
                          <FormikTextField
                            label={t('Nachricht')}
                            {...fieldProps}
                          />
                        )}
                      />
                      <FieldStackItem
                        name={`globalMessageSetting.globalMessage.isActive`}
                        render={(fieldProps) => (
                          <FormikCheckbox
                            label={t(`field.active.label`)}
                            {...fieldProps}
                          />
                        )}
                      />
                    </FieldStack>
                  </Section>
                  <Section label={t('Excel Vorlage Erprobungsübersicht')}>
                    <FlexSpinner loading={overviewTemplateIsLoading || loading}>
                      {existingOverviewTemplate && (
                        <AutoStack
                          horizontal={true}
                          tokens={{ childrenGap: 8 }}
                        >
                          <AutoStack
                            style={{ width: 200 }}
                            horizontalAlign={'center'}
                          >
                            <AutoStack horizontalAlign={'center'}>
                              <Icon
                                iconName={'ExcelDocument'}
                                styles={{
                                  root: { fontSize: 50, color: '#26764a' },
                                }}
                              />
                              <DownloadLink uri={existingOverviewTemplate.uri}>
                                {t('Vorlage Erprobungsübersicht')}
                              </DownloadLink>
                            </AutoStack>
                          </AutoStack>
                          {!overviewReuploadTemplate && (
                            <PrimaryButton
                              text={t('Vorlage aktualisieren')}
                              onClick={() => setOverviewReuploadTemplate(true)}
                              disabled={overviewTemplateIsLoading}
                            />
                          )}
                          {overviewReuploadTemplate && (
                            <DefaultButton
                              text={t('action.cancel')}
                              onClick={() => setOverviewReuploadTemplate(false)}
                              disabled={overviewTemplateIsLoading}
                            />
                          )}
                        </AutoStack>
                      )}
                      {(!existingOverviewTemplate ||
                        overviewReuploadTemplate) && (
                        <FileUpload
                          singleFile={true}
                          accept={['.xlsm', '.xlsx']}
                          parentId={'settings'}
                          parentType={'globalTemplate'}
                          onUploadCompleted={onExcelOverviewTemplateUploaded}
                        />
                      )}
                    </FlexSpinner>
                  </Section>
                  <Section label={t('Excel Vorlage Kostenverteilung')}>
                    <FlexSpinner loading={costTemplateIsLoading || loading}>
                      {existingCostTemplate && (
                        <AutoStack
                          horizontal={true}
                          tokens={{ childrenGap: 8 }}
                        >
                          <AutoStack
                            style={{ width: 200 }}
                            horizontalAlign={'center'}
                          >
                            <AutoStack horizontalAlign={'center'}>
                              <Icon
                                iconName={'ExcelDocument'}
                                styles={{
                                  root: { fontSize: 50, color: '#26764a' },
                                }}
                              />
                              <DownloadLink uri={existingCostTemplate.uri}>
                                {t('Vorlage Kostenverteilung')}
                              </DownloadLink>
                            </AutoStack>
                          </AutoStack>
                          {!overviewReuploadTemplate && (
                            <PrimaryButton
                              text={t('Vorlage aktualisieren')}
                              onClick={() => setCostReuploadTemplate(true)}
                              disabled={costTemplateIsLoading}
                            />
                          )}
                          {overviewReuploadTemplate && (
                            <DefaultButton
                              text={t('action.cancel')}
                              onClick={() => setCostReuploadTemplate(false)}
                              disabled={costTemplateIsLoading}
                            />
                          )}
                        </AutoStack>
                      )}
                      {(!existingCostTemplate || costReuploadTemplate) && (
                        <FileUpload
                          singleFile={true}
                          accept={'.xlsx'}
                          parentId={'settings'}
                          parentType={'globalTemplate'}
                          onUploadCompleted={onCostDistributionTemplateUploaded}
                        />
                      )}
                    </FlexSpinner>
                  </Section>
                  <Section label={t('Löschfrist')}>
                    <FieldStackItem
                      name="deletetionPeriodSetting.value"
                      render={(fieldProps) => (
                        <FormikTextField label={t('Jahre')} {...fieldProps} />
                      )}
                    />
                  </Section>
                </Form>
              </GeneralSettingsFormCommands>
            )}
          </Formik>
        </FlexSpinner>
      </StickyPane>
    </ViewAuthorizer>
  )
}

function getDefaultProvingTypeOptions(): ActiveProvingTypesSetting {
  const types: ProvingType[] = Object.keys(ProvingType).map(
    (k) => ProvingType[k]
  )
  return {
    type: GlobalSettingType.ActiveProvingTypes,
    typeSettings: types.map(
      (t) =>
        ({
          type: t,
          isActive: true,
        } as ProvingTypeSetting)
    ),
  } as ActiveProvingTypesSetting
}
